import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GuideHintTooltip } from './guide/GuideHintTooltip';
import type { GuideTarget } from './guide/guideContent';
import {
  ActiveGraphKind,
  AFFILIATION_TYPE_LABELS,
  DISCREDITATION_TYPE_LABELS,
  HIDDEN_ACTOR_ROLE_CATEGORIES,
  INAUTHENTIC_TYPE_LABELS,
  NodeConnection,
  SelectedNodeInfo,
  ZSCORE_ENABLED,
} from './graphTypes';
import { SCOPE_INBOUND_COLOR, SCOPE_OUTBOUND_COLOR } from './graphColors';
import { mentionTypeLabel } from './timelineUtils';
import { MemberConnectionsLine } from './MemberConnectionsLine';
import { platformNamesMapping } from '../../utils/platforms';
import { ReactComponent as AddToIcon } from '../../assets/addTo.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { ReactComponent as ChevronDownIcon } from './assets/chevron.svg';
import { ReactComponent as UserIcon } from './assets/user.svg';
import { ReactComponent as ExternalLinkIcon } from './assets/link.svg';
import { ReactComponent as HintIcon } from './assets/hint.svg';
import { FALLBACK_PLATFORM_ICON, PLATFORM_ICONS } from './platformIcons';
import { useMenuDismiss } from './useMenuDismiss';
// canonical uk/ru → name_uk locale check used by the tag components
import { isUkLocale } from '../../components/TagsTree';
import { useSocialGraphTrack } from './analytics';
import { GraphHostActions } from './hostActions';
import styles from './SocialGraphPOC.module.scss';

type CommunityStats = { size: number; density: number };

const MENTION_TYPE_DOT_COLORS: Record<string, string> = {
  MENTION: '#5b78f0',
  REPOST: '#e24a8b',
  LINK: '#2fbf74',
};
const UNKNOWN_TYPE_DOT = '#828892';

// In/out dots in this panel reuse the canvas edge hues (user decision 2026-08-20,
// overriding the blue/amber pair shown in Figma 697-1613 / ConnectionRow 691-6278)
const CONNECTION_IN_DOT = SCOPE_INBOUND_COLOR;
const CONNECTION_OUT_DOT = SCOPE_OUTBOUND_COLOR;

// "Z 4.6" readout on anomalous connection rows (sampled from Figma 697-1772)
const CONNECTION_Z_COLOR = '#e9b84a';

const ANOMALOUS_HINT =
  'Interaction intensity with this actor deviates significantly from the network average. A signal for review, not a conclusion.';

// ⓘ copy behind the "In actor group / Not in actor group" line (Figma 829-12377 / 829-12546)
const IN_ACTOR_GROUP_HINT = 'This actor is in the actor group the graph was built from.';
const NOT_IN_ACTOR_GROUP_HINT =
  'This actor is not in the actor group the graph was built from. It appears because it’s connected to actors in the group.';

// tooltip anchor for the ⓘ hints on metric rows
const METRIC_HINT_TOOLTIP_ID = 'socialGraphMetricHint';

// Hides the "Metrics" and "Behavioral metrics" sections of the actor panel
// (PM decision 2026-09-16) until the product can explain what the numbers
// mean to users. The rows, hints and i18n keys stay — flip this to restore.
const SHOW_ACTOR_METRICS = false;

// One metrics-box row (Figma MetricRow 354-205): fixed label column, value,
// ⓘ hint. The hint shows as a hover tooltip on the whole row and is repeated
// as screen-reader-only text (the row itself is not interactive).
// `guide` names the guide chapter the hint's "Learn more" opens.
const MetricRow: React.FC<{ label: string; value: string; hint?: string; guide?: GuideTarget }> = ({
  label,
  value,
  hint,
  guide,
}) => (
  <div
    className={styles.nodePanelStatRow}
    data-tooltip-id={hint ? METRIC_HINT_TOOLTIP_ID : undefined}
    data-tooltip-content={hint}
    data-guide-page={hint ? guide?.page : undefined}
    data-guide-section={hint ? guide?.section : undefined}
  >
    <span className={styles.nodePanelStatLabel}>{label}</span>
    <span className={styles.nodePanelStatValue}>{value}</span>
    {hint && (
      <>
        <HintIcon className={styles.nodePanelStatHint} aria-hidden="true" />
        <span className={styles.srOnly}>{hint}</span>
      </>
    )}
  </div>
);

export const NodePanel: React.FC<{
  info: SelectedNodeInfo;
  communityStats: CommunityStats | null;
  connections: NodeConnection[];
  // "In actor group / Not in actor group" line under the name — only meaningful
  // for graphs built from an actor group (mention graphs; not explore graphs)
  showActorGroupLabel: boolean;
  // Critical bridge has no bridge semantics on case graphs — the Categories
  // card mirrors the Actors tab and drops it there
  graphKind: ActiveGraphKind;
  onSelectActor: (nodeId: string) => void;
  onClose: () => void;
  actions: GraphHostActions | null;
}> = ({
  info,
  communityStats,
  connections,
  showActorGroupLabel,
  graphKind,
  onSelectActor,
  onClose,
  actions,
}) => {
  const { t } = useTranslation();
  const track = useSocialGraphTrack();
  const PlatformIcon = PLATFORM_ICONS[info.platform] ?? FALLBACK_PLATFORM_ICON;
  const platformLabel = platformNamesMapping[info.platform] ?? info.platform;
  // already-translated labels: the high-risk item carries its discreditation
  // types and the inauthentic items come from a label map, so translating at
  // render time would not work
  const categories = [
    ...(HIDDEN_ACTOR_ROLE_CATEGORIES.includes(info.actorRole) ? [] : [t(info.actorRole)]),
    ...(info.isKeyConnector && graphKind !== 'case' ? [t('Critical bridge (links many communities)')] : []),
    ...(info.isHighRisk
      ? [
          `${t('High Risk Actor')}${
            info.discreditations.length > 0
              ? `: ${info.discreditations.map((type) => t(DISCREDITATION_TYPE_LABELS[type] ?? type)).join(', ')}`
              : ''
          }`,
        ]
      : []),
    ...(info.isStateAffiliated ? [t('State Affiliated')] : []),
    ...(info.isHighInteraction ? [t('High-Interaction Actor')] : []),
    // at most one under current backend rules, but the contract is a list
    ...info.inauthenticTypes.map((type) => t(INAUTHENTIC_TYPE_LABELS[type] ?? type)),
  ];
  const [openActorMenuOpen, setOpenActorMenuOpen] = useState(false);
  const openActorRef = useRef<HTMLDivElement>(null);
  const openActorTriggerRef = useRef<HTMLButtonElement>(null);

  // Info / Connections tabs (Figma PanelTabs 692-6248). The page remounts the
  // panel per actor (key=nodeId), so a new selection always opens on Info
  const [activeTab, setActiveTab] = useState<'info' | 'connections'>('info');
  const infoTabRef = useRef<HTMLButtonElement>(null);
  const connectionsTabRef = useRef<HTMLButtonElement>(null);
  // direction drill from the Info tab's Connections card: clicking a stat opens
  // the Connections tab narrowed to that direction; cleared by "Show all" or by
  // re-activating the Connections tab directly. Resets with the per-actor remount.
  const [connectionsFilter, setConnectionsFilter] = useState<'in' | 'out' | 'mutual' | null>(null);

  const openConnectionsFiltered = (filter: 'in' | 'out' | 'mutual') => {
    setConnectionsFilter(filter);
    setActiveTab('connections');
    // the Info tabpanel (and with it the clicked stat button) unmounts on the
    // switch — park focus on the Connections tab so keyboard users aren't
    // dropped to <body>
    connectionsTabRef.current?.focus();
  };

  const tabKeys = (event: React.KeyboardEvent) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 'info'
        : event.key === 'End'
          ? 'connections'
          : activeTab === 'info'
            ? 'connections'
            : 'info';
    setActiveTab(next);
    (next === 'info' ? infoTabRef : connectionsTabRef).current?.focus();
  };

  useMenuDismiss(openActorMenuOpen, openActorRef, openActorTriggerRef, () =>
    setOpenActorMenuOpen(false),
  );

  const selectOpenActorAction = (target: 'platform' | 'osavul', url?: string | null) => {
    setOpenActorMenuOpen(false);
    openActorTriggerRef.current?.focus();
    if (!url) return;
    // sourceUrl comes from scraped platform data — only ever open web schemes
    // (or our own relative paths), never javascript:/data:/etc.
    if (!/^https?:\/\//i.test(url) && !url.startsWith('/')) return;
    track('sg_actor_opened_externally', { target, platform: info.platform });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const focusOpenActorItem = (fromIndex: number, direction: 1 | -1) => {
    const items = openActorRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)');
    if (!items || items.length === 0) return;
    const nextIndex = (fromIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  const hasBehavioralMetrics =
    SHOW_ACTOR_METRICS &&
    ((ZSCORE_ENABLED && (info.avgZScore !== null || info.weightedAnomalyScore !== null)) ||
      info.reciprocity !== null ||
      info.clusteringCoeff !== null ||
      info.platformDiversity !== null ||
      info.bridgeScore !== null ||
      info.weightGini !== null);
  const mentionEntries = Object.entries(info.mentionCounts);

  // 'in'/'out' drills include mutual pairs (a mutual neighbor DID reach the
  // actor and was reached) — that also keeps the list length equal to the
  // inbound/outbound connection counts on the card that opened the drill
  const visibleConnections = connections.filter(
    (connection) =>
      connectionsFilter === null ||
      connection.direction === connectionsFilter ||
      (connectionsFilter !== 'mutual' && connection.direction === 'mutual'),
  );
  // Connections tab grouping (designer spec): anomalous ties first as their own
  // group; the group headers disappear when there is only one group to name
  const anomalousConnections = visibleConnections.filter((connection) => connection.anomalous);
  const otherConnections = visibleConnections.filter((connection) => !connection.anomalous);

  const renderConnectionList = (items: NodeConnection[]) => (
    <ul className={styles.connectionsList}>
      {items.map((connection) => {
        const RowPlatformIcon = PLATFORM_ICONS[connection.platform] ?? FALLBACK_PLATFORM_ICON;
        return (
          // the + button can't nest inside the row button, so the li is the
          // flex row holding both siblings
          <li key={connection.id} className={styles.connectionRowWrap}>
            <button
              type="button"
              className={styles.connectionRow}
              onClick={() => onSelectActor(connection.id)}
            >
              <span
                className={styles.connectionRowIcon}
                role="img"
                aria-label={platformNamesMapping[connection.platform] ?? connection.platform}
              >
                <RowPlatformIcon aria-hidden="true" />
              </span>
              <span className={styles.connectionRowText}>
                <span className={styles.connectionRowName}>{connection.label}</span>
                {/* the pair's interactions per direction (design 2026-10-16):
                    "1 Inbound – 6 interactions"; a one-way relation is a single
                    line, a mutual pair shows inbound, outbound and a Mutual line
                    with the pair's total. Between two actors there is at most
                    one connection per direction, hence the 1 */}
                <MemberConnectionsLine
                  connections={{
                    ...(connection.direction !== 'out'
                      ? { inbound: { connections: 1, interactions: connection.inboundCount } }
                      : {}),
                    ...(connection.direction !== 'in'
                      ? { outbound: { connections: 1, interactions: connection.outboundCount } }
                      : {}),
                    ...(connection.direction === 'mutual'
                      ? { mutual: { connections: 1, interactions: connection.strength } }
                      : {}),
                  }}
                />
              </span>
              {connection.anomalous && connection.zScore !== null && (
                <span className={styles.connectionRowZ} style={{ color: CONNECTION_Z_COLOR }}>
                  {`Z ${connection.zScore.toFixed(1)}`}
                </span>
              )}
            </button>
            {actions && (
              <button
                type="button"
                className={styles.connectionRowAdd}
                onClick={() => actions.addToGroup([connection.id])}
                aria-label={t('Add {{name}} to group', { name: connection.label })}
              >
                <AddToIcon aria-hidden="true" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <aside className={styles.nodePanel} aria-label={t('Node details for {{name}}', { name: info.label })}>
      <div className={styles.nodePanelHeader}>
        <h2 className={styles.nodePanelTitle}>{t('Actor')}</h2>
        <button
          type="button"
          className={styles.nodePanelClose}
          onClick={onClose}
          aria-label={t('Close node details')}
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>
      <div className={styles.nodePanelHead}>
        <div className={styles.nodePanelIdLine}>
          <span className={styles.nodePanelPlatform} role="img" aria-label={platformLabel}>
            <PlatformIcon aria-hidden="true" />
          </span>
          <p className={styles.nodePanelName}>{info.label}</p>
        </div>
        {/* membership in the actor group the graph was built from (Figma
            826-17141 / 826-17300); replaces the old "In scope / External"
            Categories item. The bubble opens leftward like the metric hints. */}
        {showActorGroupLabel && (
          <p
            className={styles.nodePanelScopeLine}
            data-tooltip-id={METRIC_HINT_TOOLTIP_ID}
            data-tooltip-content={t(info.inEvent ? IN_ACTOR_GROUP_HINT : NOT_IN_ACTOR_GROUP_HINT)}
            data-guide-page="how-to-read"
            data-guide-section="in-actor-group"
          >
            {info.inEvent ? t('In actor group') : t('Not in actor group')}
            <HintIcon className={styles.actorGroupHint} aria-hidden="true" />
            <span className={styles.srOnly}>
              {t(info.inEvent ? IN_ACTOR_GROUP_HINT : NOT_IN_ACTOR_GROUP_HINT)}
            </span>
          </p>
        )}
        <div className={styles.nodePanelControls}>
          <div className={styles.nodePanelOpenActorWrap} ref={openActorRef}>
            <button
              type="button"
              ref={openActorTriggerRef}
              className={styles.nodePanelControlBtn}
              onClick={() => setOpenActorMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={openActorMenuOpen}
            >
              <UserIcon aria-hidden="true" />
              {t('Actor profile')}
              <ChevronDownIcon aria-hidden="true" className={styles.nodePanelControlChevron} />
            </button>
              {openActorMenuOpen && (
                <div className={styles.nodePanelOpenActorMenu} role="menu" aria-label={t('Actor profile')}>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.nodePanelOpenActorItem}
                    disabled={!info.sourceUrl}
                    onClick={() => selectOpenActorAction('platform', info.sourceUrl)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowDown') { event.preventDefault(); focusOpenActorItem(0, 1); }
                      if (event.key === 'ArrowUp') { event.preventDefault(); focusOpenActorItem(0, -1); }
                    }}
                  >
                    {t('Open on {{platform}}', { platform: platformLabel })}
                    <ExternalLinkIcon aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.nodePanelOpenActorItem}
                    onClick={() => selectOpenActorAction('osavul', `/sources/${info.nodeId}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowDown') { event.preventDefault(); focusOpenActorItem(1, 1); }
                      if (event.key === 'ArrowUp') { event.preventDefault(); focusOpenActorItem(1, -1); }
                    }}
                  >
                    {t('Open on Osavul')}
                    <ExternalLinkIcon aria-hidden="true" />
                  </button>
                </div>
              )}
          </div>
          {actions && (
            <button
              type="button"
              className={styles.nodePanelControlBtn}
              onClick={() => actions.addToGroup([info.nodeId])}
            >
              <AddToIcon aria-hidden="true" />
              {t('Add to group')}
            </button>
          )}
        </div>
      </div>
      {/* Info / Connections strip carries the head rule; the active tab sits on
          it as a 2px underline (Figma PanelTabs 692-6248) */}
      <div className={styles.nodePanelTabs} role="tablist" aria-label={t('Actor details')}>
        <button
          type="button"
          role="tab"
          ref={infoTabRef}
          id="socialGraphActorTab-info"
          aria-selected={activeTab === 'info'}
          aria-controls="socialGraphActorPanel-info"
          tabIndex={activeTab === 'info' ? 0 : -1}
          className={`${styles.nodePanelTab} ${activeTab === 'info' ? styles.nodePanelTabActive : ''}`}
          onClick={() => setActiveTab('info')}
          onKeyDown={tabKeys}
        >
          {t('Info')}
        </button>
        <button
          type="button"
          role="tab"
          ref={connectionsTabRef}
          id="socialGraphActorTab-connections"
          aria-selected={activeTab === 'connections'}
          aria-controls="socialGraphActorPanel-connections"
          tabIndex={activeTab === 'connections' ? 0 : -1}
          className={`${styles.nodePanelTab} ${activeTab === 'connections' ? styles.nodePanelTabActive : ''}`}
          onClick={() => {
            setActiveTab('connections');
            // activating the tab itself always means the full list — only the
            // Info-card stats open it pre-filtered
            setConnectionsFilter(null);
          }}
          onKeyDown={tabKeys}
        >
          {t('Connections')}
          <span className={styles.nodePanelTabCount}>{connections.length}</span>
        </button>
      </div>
      {activeTab === 'connections' && (
        <div
          className={styles.nodePanelBody}
          role="tabpanel"
          id="socialGraphActorPanel-connections"
          aria-labelledby="socialGraphActorTab-connections"
        >
          {connections.length === 0 && <p className={styles.overviewNote}>{t('No connections')}</p>}
          {connectionsFilter !== null && (
            <div className={styles.connectionsFilterRow}>
              <span className={styles.nodePanelSectionTitle}>
                {connectionsFilter === 'in'
                  ? t('Inbound connections')
                  : connectionsFilter === 'out'
                    ? t('Outbound connections')
                    : t('Mutual connections')}
              </span>
              <span className={styles.connectionsGroupCount}>{visibleConnections.length}</span>
              <button
                type="button"
                className={styles.connectionsFilterClear}
                onClick={() => setConnectionsFilter(null)}
              >
                {t('Show all')}
              </button>
            </div>
          )}
          {actions && visibleConnections.length > 0 && (
            <button
              type="button"
              className={`${styles.nodePanelControlBtn} ${styles.connectionsAddAllBtn}`}
              onClick={() => actions.addToGroup(visibleConnections.map((connection) => connection.id))}
            >
              <AddToIcon aria-hidden="true" />
              {t('Add all to group')}
            </button>
          )}
          {anomalousConnections.length > 0 && (
            <div className={styles.nodePanelSection}>
              <div
                className={styles.connectionsGroupHead}
                data-tooltip-id={METRIC_HINT_TOOLTIP_ID}
                data-tooltip-content={t(ANOMALOUS_HINT)}
                data-guide-page="calculations"
              >
                <span className={styles.nodePanelSectionTitle}>{t('Anomalous connections')}</span>
                <HintIcon className={styles.actorGroupHint} aria-hidden="true" />
                <span className={styles.srOnly}>{t(ANOMALOUS_HINT)}</span>
                <span className={styles.connectionsGroupCount}>{anomalousConnections.length}</span>
              </div>
              {renderConnectionList(anomalousConnections)}
            </div>
          )}
          {otherConnections.length > 0 &&
            (anomalousConnections.length > 0 ? (
              <div className={styles.nodePanelSection}>
                <div className={styles.connectionsGroupHead}>
                  <span className={styles.nodePanelSectionTitle}>{t('Other connections')}</span>
                  <span className={styles.connectionsGroupCount}>{otherConnections.length}</span>
                </div>
                {renderConnectionList(otherConnections)}
              </div>
            ) : (
              renderConnectionList(otherConnections)
            ))}
        </div>
      )}
      {activeTab === 'info' && (
      <div
        className={styles.nodePanelBody}
        role="tabpanel"
        id="socialGraphActorPanel-info"
        aria-labelledby="socialGraphActorTab-info"
      >
        {/* dot + text items in one card (Figma nc-box 371-3728) — replaced the
            old colored chip badges. Same hidden set as the Actors tab (PM
            decision 2026-09-17); the whole section goes when nothing is left */}
        {categories.length > 0 && (
          <div className={styles.nodePanelSection}>
            <span className={styles.nodePanelSectionTitle}>{t('Categories')}</span>
            <div className={`${styles.nodePanelDotCard} ${styles.nodePanelCategoriesCard}`}>
              {categories.map((label) => (
                <span key={label} className={styles.nodePanelDotItem}>
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
        {info.affiliations.length > 0 && (
          <div className={styles.nodePanelSection}>
            <span className={styles.nodePanelSectionTitle}>{t('State affiliated')}</span>
            <div className={styles.nodePanelDotCard}>
              {info.affiliations.map((affiliation, index) => (
                <span
                  className={styles.nodePanelDotItem}
                  key={`${affiliation.entityType}-${affiliation.country ?? ''}-${index}`}
                >
                  {t(AFFILIATION_TYPE_LABELS[affiliation.entityType] ?? affiliation.entityType)}
                  {affiliation.country ? ` · ${affiliation.country}` : ''}
                </span>
              ))}
            </div>
          </div>
        )}
        {info.tags.length > 0 && (
          <div className={styles.nodePanelSection}>
            <span className={styles.nodePanelSectionTitle}>{t('Tags')}</span>
            <div className={styles.nodePanelDotCard}>
              {info.tags.map((tag) => (
                <span className={styles.nodePanelDotItem} key={tag.tag}>
                  {(isUkLocale() ? tag.nameUk || tag.nameEn : tag.nameEn || tag.nameUk) || tag.tag}
                </span>
              ))}
            </div>
          </div>
        )}
        {(info.audience !== null || info.originCountry !== null || info.isVerified !== null) && (
          <div className={styles.nodePanelSection}>
            <span className={styles.nodePanelSectionTitle}>{t('Profile')}</span>
            <div className={styles.nodePanelStatBox}>
              {info.audience !== null && (
                <MetricRow label={t('Audience')} value={info.audience.toLocaleString()} />
              )}
              {info.originCountry !== null && (
                <MetricRow label={t('Origin country')} value={info.originCountry} />
              )}
              {info.isVerified !== null && (
                <MetricRow
                  label={t('Verification')}
                  value={info.isVerified ? t('Verified') : t('Not verified')}
                />
              )}
            </div>
          </div>
        )}
        <div className={styles.nodePanelSection}>
          <span className={styles.nodePanelSectionTitle}>{t('Community')}</span>
          <div className={styles.nodePanelCommunityCard}>
            <div className={styles.nodePanelCommunityInfo}>
              <p className={styles.nodePanelCommunityName}>{t('Community #{{id}}', { id: info.community })}</p>
              {/* density is hidden for now (2026-09-25) — the value stays in
                  communityStats so the line can come back without a rebuild */}
              {communityStats && (
                <p className={styles.nodePanelCommunityMeta}>
                  {t('Actors: {{count}}', { count: communityStats.size })}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className={styles.nodePanelSection}>
          <span className={styles.nodePanelSectionTitle}>{t('Connections')}</span>
          {/* each stat drills into the Connections tab filtered to its
              direction; zero-count stats stay plain text — a drill into an
              empty list helps nobody, and the global button:disabled
              !important styles would mangle a disabled look anyway */}
          <div className={styles.nodePanelDotCard}>
            {[
              {
                filter: 'in' as const,
                label: t('Inbound'),
                count: info.connections.inbound.connections,
                glyph: (
                  <span
                    className={styles.nodePanelDot}
                    style={{ backgroundColor: CONNECTION_IN_DOT }}
                    aria-hidden="true"
                  />
                ),
              },
              {
                filter: 'out' as const,
                label: t('Outbound'),
                count: info.connections.outbound.connections,
                glyph: (
                  <span
                    className={styles.nodePanelDot}
                    style={{ backgroundColor: CONNECTION_OUT_DOT }}
                    aria-hidden="true"
                  />
                ),
              },
              {
                filter: 'mutual' as const,
                label: t('Mutual'),
                count: info.connections.mutual.connections,
                glyph: (
                  <span className={styles.nodePanelDotPair} aria-hidden="true">
                    <span />
                    <span />
                  </span>
                ),
              },
            ].map(({ filter, label, count, glyph }) =>
              count > 0 ? (
                <button
                  key={filter}
                  type="button"
                  className={`${styles.nodePanelDotItem} ${styles.nodePanelDotItemBtn}`}
                  onClick={() => openConnectionsFiltered(filter)}
                >
                  {glyph}
                  {label} {count}
                </button>
              ) : (
                <span key={filter} className={styles.nodePanelDotItem}>
                  {glyph}
                  {label} {count}
                </span>
              ),
            )}
          </div>
        </div>
        {mentionEntries.length > 0 && (
          <div className={styles.nodePanelSection}>
            <span className={styles.nodePanelSectionTitle}>{t('Interactions')}</span>
            <div className={styles.nodePanelDotCard}>
              {mentionEntries.map(([type, count]) => (
                <span className={styles.nodePanelDotItem} key={type}>
                  <span
                    className={styles.nodePanelDot}
                    style={{ backgroundColor: MENTION_TYPE_DOT_COLORS[type] ?? UNKNOWN_TYPE_DOT }}
                    aria-hidden="true"
                  />
                  {mentionTypeLabel(t, type)} {count}
                </span>
              ))}
            </div>
          </div>
        )}
        {SHOW_ACTOR_METRICS && (
          <div className={styles.nodePanelSection}>
            <span className={styles.nodePanelSectionTitle}>{t('Metrics')}</span>
            <div className={styles.nodePanelStatBox}>
              <MetricRow
                label={t('PageRank')}
                value={info.pagerank.toFixed(4)}
                hint={t('How influential the actor is based on who references it — links from influential actors weigh more.')}
                guide={{ page: 'calculations', section: 'pagerank' }}
              />
              <MetricRow
                label={t('Betweenness')}
                value={info.betweenness.toFixed(4)}
                hint={t('How often the actor sits on the shortest paths between others — high values mean a gatekeeper position.')}
                guide={{ page: 'calculations' }}
              />
              <MetricRow
                label={t('Hub / Authority')}
                value={`${info.hubScore.toFixed(3)} / ${info.authorityScore.toFixed(3)}`}
                hint={t('Hub — actively references key sources; Authority — is referenced by key hubs.')}
                guide={{ page: 'calculations' }}
              />
            </div>
          </div>
        )}
        {hasBehavioralMetrics && (
          <div className={styles.nodePanelSection}>
            <span className={styles.nodePanelSectionTitle}>{t('Behavioral metrics')}</span>
            <div className={styles.nodePanelStatBox}>
              {/* the two z-derived rows honor the ZSCORE_ENABLED kill-switch */}
              {ZSCORE_ENABLED && info.avgZScore !== null && (
                <MetricRow
                  label={t('Avg Z-Score')}
                  value={info.avgZScore.toFixed(2)}
                  hint={t('Average anomaly of the actor’s interaction intensity versus its baseline.')}
              guide={{ page: 'calculations' }}
                />
              )}
              {ZSCORE_ENABLED && info.weightedAnomalyScore !== null && (
                <MetricRow
                  label={t('Anomaly score')}
                  value={info.weightedAnomalyScore.toFixed(2)}
                  hint={t('Combined anomaly across the actor’s relations, weighted by interaction volume.')}
              guide={{ page: 'calculations' }}
                />
              )}
              {info.reciprocity !== null && (
                <MetricRow
                  label={t('Reciprocity')}
                  value={info.reciprocity.toFixed(2)}
                  hint={t('Share of the actor’s relations that are mutual.')}
              guide={{ page: 'calculations' }}
                />
              )}
              {info.clusteringCoeff !== null && (
                <MetricRow
                  label={t('Clustering coeff.')}
                  value={info.clusteringCoeff.toFixed(2)}
                  hint={t('How tightly the actor’s neighbors are connected to each other.')}
              guide={{ page: 'calculations' }}
                />
              )}
              {info.platformDiversity !== null && (
                <MetricRow
                  label={t('Platform diversity')}
                  value={info.platformDiversity.toFixed(2)}
                  hint={t('How evenly the actor’s activity spreads across platforms.')}
              guide={{ page: 'calculations' }}
                />
              )}
              {info.bridgeScore !== null && (
                <MetricRow
                  label={t('Bridge score')}
                  value={info.bridgeScore.toFixed(2)}
                  hint={t('How much the actor connects otherwise separate parts of the network.')}
              guide={{ page: 'calculations' }}
                />
              )}
              {info.weightGini !== null && (
                <MetricRow
                  label={t('Weight Gini')}
                  value={info.weightGini.toFixed(2)}
                  hint={t('How unevenly the actor’s interaction volume is distributed across its relations.')}
              guide={{ page: 'calculations' }}
                />
              )}
            </div>
          </div>
        )}
      </div>
      )}
      {/* the panel is flush to the window edge, so the bubble opens leftward
          with the tail on its right (Figma Tooltip/Hint, Tail=Right) */}
      <GuideHintTooltip id={METRIC_HINT_TOOLTIP_ID} place="left" />
    </aside>
  );
};
